import { useEffect, useRef } from "react"
import { useNavigation, useRoute } from "@react-navigation/native"
import { Platform } from "react-native"
import { useSummerFunnel } from "../context/SummerFunnelContext"

const IS_WEB = Platform.OS === "web"

/**
 * Lit le param `user` (base64 email) — d'abord depuis les params de route
 * (propagés via navigation.navigate), sinon depuis l'URL web (?user=…). C'est
 * l'identité du lead, présente sur le lien magique d'entrée et que l'on
 * propage sur chaque étape pour rendre le funnel partageable par URL.
 */
export const readUserParam = (route) =>
    route?.params?.user ??
    (IS_WEB && typeof window !== "undefined"
        ? new URLSearchParams(window.location?.search || "").get("user")
        : null)

/**
 * Lit un param booléen de lien fast-track (`?engagement=1`, `?sans_depot=1`) —
 * d'abord depuis les params de route, sinon depuis l'URL web. Retourne `true`
 * pour "1"/"true", `false` pour "0"/"false", et `null` quand le param est ABSENT
 * (pour distinguer "non fourni" de "fourni à false"). Sert à un lien admin fabriqué
 * à la main avant le générateur BO (P-1971) ; sinon le choix vient de l'état
 * fast-track (`checkout_params`) hydraté à la reprise.
 */
export const readBoolParam = (route, name) => {
    const raw =
        route?.params?.[name] ??
        (IS_WEB && typeof window !== "undefined"
            ? new URLSearchParams(window.location?.search || "").get(name)
            : null)
    if (raw == null) return null
    if (raw === true || raw === "1" || raw === "true") return true
    if (raw === false || raw === "0" || raw === "false") return false
    return null
}

/**
 * Params à propager sur chaque `navigation.navigate` aval pour garder l'URL
 * partageable (`?user=…`). `undefined` quand il n'y a pas de `user` (pas de
 * params parasites sur la route).
 */
export const userNavParams = (userParam) =>
    userParam ? { user: userParam } : undefined

/**
 * Écrit `?user=…` dans l'URL web courante (sans recharger ni empiler dans
 * l'historique) pour que l'URL de l'étape soit partageable telle quelle
 * (ex : l'admin copie l'URL en cours et le parent l'ouvre). React Navigation
 * ne reflète pas les query params dans la barre d'URL après un
 * navigation.navigate, d'où ce replaceState ciblé.
 */
const ensureUserInUrl = (userParam) => {
    if (!IS_WEB || typeof window === "undefined") return
    try {
        const url = new URL(window.location.href)
        if (url.searchParams.get("user") === userParam) return
        url.searchParams.set("user", userParam)
        window.history.replaceState(window.history.state, "", url.toString())
    } catch (_e) {
        /* noop — replaceState indisponible (très vieux navigateur) */
    }
}

/**
 * Un funnel « froid » = aucune donnée de parcours en mémoire. Survient après un
 * hard-refresh dans un onglet sans sessionStorage (rare) ou, surtout, quand une
 * URL d'étape est ouverte dans un contexte neuf / navigation privée (l'admin
 * copie l'URL, le parent l'ouvre). On ne se fie pas au seul funnelStateId :
 * l'écran classe peut avoir un prénom/email pré-rempli sans encore d'id.
 */
const isColdContext = (state) =>
    !state?.funnelStateId &&
    !state?.studentClass &&
    !state?.parentEmail &&
    !state?.paid

/**
 * Garde unique partagée par tous les écrans du funnel Cours d'été pour le rendre
 * refresh-safe et partageable par URL :
 *
 *  1. Propage `?user=` dans l'URL web de chaque étape (URL partageable).
 *  2. Sur cold-load (contexte vide après refresh / ouverture externe) AVEC un
 *     `user` → ré-hydrate en repassant par `summer/resume?user=…`, qui rejoue le
 *     chemin de reprise existant (hydrate le Context + atterrit sur la bonne
 *     étape via STATE_TO_STACK). On fait toujours confiance à l'ÉTAT réel : la
 *     reprise atterrit sur l'étape la plus avancée du lead, jamais sur l'étape
 *     de l'URL si celle-ci est en avance.
 *  3. Sur cold-load SANS `user` → retour au début du funnel (pas de page
 *     blanche, pas de crash).
 *
 * `isStart` exempte l'écran de départ de la redirection « retour au début »
 * (il EST le début) ; il déclenche quand même la reprise quand un `user` est
 * présent sur un contexte froid.
 */
export const useSummerFunnelGuard = ({ isStart = false } = {}) => {
    const navigation = useNavigation()
    const route = useRoute()
    const { state } = useSummerFunnel()
    const handledRef = useRef(false)

    const userParam = readUserParam(route)

    useEffect(() => {
        // Garde l'URL partageable à jour à chaque rendu pertinent.
        if (userParam) ensureUserInUrl(userParam)
    }, [userParam])

    useEffect(() => {
        // Une seule décision de routage par montage d'écran.
        if (handledRef.current) return
        if (!isColdContext(state)) return

        handledRef.current = true

        if (userParam) {
            // Ré-hydrate via le chemin de reprise existant : il pose le Context
            // puis reset() vers l'étape réelle de l'état (trust STATE > URL).
            navigation.reset({
                index: 0,
                routes: [{ name: "summer/resume", params: { user: userParam } }],
            })
            return
        }

        // Contexte vide et aucune identité dans l'URL → retour au début
        // (jamais d'écran blanc). L'écran de départ ne se redirige pas lui-même.
        if (!isStart) {
            navigation.reset({ index: 0, routes: [{ name: "summer/start" }] })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state, userParam])

    // `nextParams` est prêt à passer tel quel à navigation.navigate(next, …)
    // pour propager `?user=` (ou `undefined` quand il n'y a pas de lead).
    return { userParam, nextParams: userNavParams(userParam) }
}
